import { Injectable, OnDestroy } from '@angular/core';
import { BehaviorSubject, Observable, Subject, Subscription } from 'rxjs';
import { distinctUntilChanged, map } from 'rxjs/operators';
import { FirebaseAuthService } from 'src/app/shared/providers/firebase-auth.service';
import { ProductAnalyticsService, ProductEventName, ProductEventParams, productErrorCode, productMegabytes } from 'src/app/shared/services/product-analytics.service';
import { RenderBackend, RenderBackendKind, RenderJobFacts, RenderRunEvent, RenderUploadEvent, simulateUpload } from './render-backend';
import {
  RENDER_FLOW_STEPS,
  RENDER_VIDEO_LINES,
  RenderCheckoutError,
  RenderCheckoutSession,
  RenderFlowNotice,
  RenderFlowStep,
  RenderJob,
  RenderJobSource,
  RenderModelGoneError,
  RenderNotImplementedError,
  renderPreviewCreep,
  RenderPreviewResult,
  RenderRunError,
  RenderVideosResult,
} from './render-flow.model';
import { RenderUploadFile, RenderUploadModel } from './render-upload.inspector';
import { RENDER_PREVIEW_STAGES, RENDER_UPLOAD_BAR, RenderSample, renderSampleById } from './render.data';

// The render flow's state: every model the user has put through it, which step each one is on,
// and the one-shot notices between steps. Pages read jobs from here and call the actions; the I/O
// goes through RenderBackend. Jobs persist in sessionStorage so a reload (or the return from Stripe
// Checkout) lands on the same step — the prototype's sessionStorage.threeDWayDesign, with real
// steps. A backend that keeps the user's jobs (the dev render simulator's Firestore list) is the
// source of truth on top of that: another tab, another device or a closed browser loses nothing
// (reconcile).
//
// The service never navigates: the flow shell watches the current job and moves the router to
// /render/<step>/:modelId when the step changes.

const STORAGE_KEY = '3dway.render-flow.v1';
// A Stripe checkout session lives a day; a cancel after that is no return from it.
const CHECKOUT_RETURN_MS = 24 * 60 * 60 * 1000;
// How long the return from Stripe waits for the backend's list in a tab without the job.
const CHECKOUT_LIST_WAIT_MS = 6000;
// How often a followed preview's bar creeps (the bar eases each step over 0.6 s).
const CREEP_TICK_MS = 1000;

export type RenderPreviewInput = { sample: RenderSample } | { upload: RenderUploadModel };

// What beginPurchase() decided: leave for a hosted checkout, or stay in the flow.
export type RenderPurchaseNext = { kind: 'redirect'; url: string } | { kind: 'step'; step: RenderFlowStep };

// The user's models at work right now (an upload, a preview or the videos): how many, how far
// along on average (each job's bar), and where to see them — the model itself when there is one,
// the library otherwise.
export interface RenderActivity {
  count: number;
  progress: number;
  url: string;
  // One of them is still uploading: this tab has to stay open for it.
  uploading: boolean;
}

// Results that came in while the user was elsewhere and are not opened yet — a preview, the
// videos, or a failure of either: how many, what the one is, where to see them. Failures come
// first: they need the user.
export interface RenderReady {
  count: number;
  what: 'preview' | 'videos';
  failed: boolean;
  url: string;
}

// What the upload step has in flight before an upload starts (RenderFlowService.uploading$): an
// archive being opened. The upload itself is a job's (RenderJob.uploading).
export type RenderUploading = 'unpacking' | null;

interface StoredState {
  jobs: RenderJob[];
}

@Injectable()
export class RenderFlowService implements OnDestroy {
  private readonly jobsSubject = new BehaviorSubject<RenderJob[]>([]);
  public readonly jobs$ = this.jobsSubject.asObservable();

  // Shown once by the next step's page (prototype's flowMessage), then cleared.
  private notice: RenderFlowNotice | null = null;
  // Files dropped on the landing: the upload step opens, inspects and uploads them on arrival, or
  // right away if it is open (files$).
  private pendingFiles: RenderUploadFile[] | null = null;
  private readonly filesSubject = new Subject<void>();
  public readonly files$ = this.filesSubject.asObservable();
  // A sample chosen on the landing ("Try with this model"): the upload step plays its
  // upload on arrival, as if it had been dropped into the zone there.
  private pendingSample: RenderSample | null = null;
  // The upload step's archive being opened, for the tab's title; null when nothing is.
  private readonly uploadingSubject = new BehaviorSubject<RenderUploading>(null);
  public readonly uploading$ = this.uploadingSubject.pipe(distinctUntilChanged());
  // The preview that has just finished: its step develops the frames on the screens they were
  // awaited on, instead of showing them outright.
  private revealModelId: string | null = null;
  // The model whose page is open (the shell says). A run that ends anywhere else leaves its job
  // `unseen` — a blue dot on its library card.
  private viewingModelId: string | null = null;

  // File objects of upload jobs, this page load only (not persisted).
  private readonly uploads = new Map<string, RenderUploadModel>();
  // Each job's work in this tab: its upload, then its run (the preview or the videos).
  private readonly runs = new Map<string, Subscription>();
  private readonly signedOutSubscription: Subscription;

  // The backend's list of the user's jobs, when it keeps one (not the mock): the last one received,
  // and whether one has arrived since the sign-in — a step URL opened in a new tab waits for it
  // (RenderJobGuard). Jobs are keyed by the backend's id, so a URL outlives the tab.
  private readonly listSubscription?: Subscription;
  private listedJobs: RenderJob[] | null = null;
  private readonly listedSubject = new BehaviorSubject<boolean>(false);
  // Every id the list has shown: one that leaves it was deleted (a cancel in another tab).
  private readonly everListed = new Set<string>();
  // Models removed here whose documents the list still shows: the document goes only after
  // `stop_render` and `del_model` (seconds), and any list in between — stop_render's own write,
  // another model's progress — brought the card back as if the removal had failed (designer,
  // 2026-11-02: "не могу удалить"). Kept off the page until the list lets them go; a removal that
  // failed lets them back.
  private readonly leaving = new Set<string>();
  private uploadFailure: string | null = null;
  // The dev renderer's previews followed in this tab: the bar's floor from the last report and when
  // it was reached. The bar creeps on from there (renderPreviewCreep) while nothing new comes in.
  private readonly creeps = new Map<string, { floor: number; since: number }>();
  private creepTimer?: ReturnType<typeof setInterval>;

  // Closing the tab, reloading, typing another address while a model uploads: the browser asks with
  // its own words. Moving around the site never stops an upload — it runs here, not in a page.
  private readonly onBeforeUnload = (event: BeforeUnloadEvent): void => {
    if (this.jobs.some((job) => job.uploading)) {
      event.preventDefault();
      // Older Chrome and Safari ask only when this is set.
      event.returnValue = '';
    }
  };

  constructor(
    private readonly backend: RenderBackend,
    private readonly firebaseAuth: FirebaseAuthService,
    private readonly productAnalytics: ProductAnalyticsService,
  ) {
    window.addEventListener('beforeunload', this.onBeforeUnload);
    this.restore();
    this.listSubscription = this.backend.watchJobs()?.subscribe((jobs) => this.reconcile(jobs));
    // The jobs are the signed-in user's: whoever signs in next in this tab starts with none.
    this.signedOutSubscription = this.firebaseAuth.notLoggedUserSubscription.subscribe(() => {
      this.listedJobs = null;
      this.everListed.clear();
      this.leaving.clear();
      this.listedSubject.next(false);
      if (this.jobs.length) {
        this.resetAll();
      }
    });
  }

  ngOnDestroy(): void {
    window.removeEventListener('beforeunload', this.onBeforeUnload);
    clearInterval(this.creepTimer);
    this.signedOutSubscription.unsubscribe();
    this.listSubscription?.unsubscribe();
    this.runs.forEach((run) => run.unsubscribe());
    this.runs.clear();
  }

  // --- reading ---

  public get jobs(): RenderJob[] {
    return this.jobsSubject.getValue();
  }

  public job(modelId: string): RenderJob | undefined {
    return this.jobs.find((item) => item.modelId === modelId);
  }

  // The number on Library in the profile menu (designer, Figma 28547:27273 — "12" on a preview
  // page; moved into the menu 2026-09-25): the
  // user's rendered models, preview or videos. It grows as each parallel preview finishes; a model
  // still in its first render, or whose preview failed, has nothing to show yet and is not counted.
  public readonly renderedCount$: Observable<number> = this.jobs$.pipe(
    map((jobs) => RenderFlowService.ownJobs(jobs).filter((job) => RenderFlowService.isRendered(job)).length),
    distinctUntilChanged(),
  );

  // What the header's ring fills with while something renders.
  public readonly activity$: Observable<RenderActivity | null> = this.jobs$.pipe(
    map((jobs): RenderActivity | null => {
      const active = jobs.filter((job) => job.step === 'processing' || job.step === 'rendering');
      if (!active.length) {
        return null;
      }
      const progress = Math.round(active.reduce((sum, job) => sum + job.progress, 0) / active.length);
      return {
        count: active.length,
        progress,
        url: active.length === 1 ? RenderFlowService.stepUrl(active[0]) : '/render/library',
        uploading: active.some((job) => !!job.uploading),
      };
    }),
    distinctUntilChanged((a, b) => a?.count === b?.count && a?.progress === b?.progress && a?.url === b?.url && a?.uploading === b?.uploading),
  );

  // What the profile button's dot announces once a render is done (owner, 2026-09-30: the ring while
  // it renders, a sign when there is something to look at — red when it failed; 2026-10-20: the dot,
  // no longer a pill). Opening the model clears it.
  public readonly ready$: Observable<RenderReady | null> = this.jobs$.pipe(
    map((jobs): RenderReady | null => {
      const unseen = RenderFlowService.ownJobs(jobs).filter((job) => job.unseen);
      const failed = unseen.filter((job) => job.step === 'error');
      const ready = failed.length ? failed : unseen.filter((job) => job.step === 'preview' || job.step === 'downloads');
      if (!ready.length) {
        return null;
      }
      const [first] = ready;
      const videos = failed.length ? first.error?.failedStep === 'rendering' : first.step === 'downloads';
      return {
        count: ready.length,
        what: videos ? 'videos' : 'preview',
        failed: failed.length > 0,
        url: ready.length === 1 ? RenderFlowService.stepUrl(first) : '/render/library',
      };
    }),
    distinctUntilChanged((a, b) => a?.count === b?.count && a?.what === b?.what && a?.failed === b?.failed && a?.url === b?.url),
  );

  // The user's own models: the library, its count and the profile button's dot. A sample walked
  // through is not one of them (owner, 2026-10-23) — it is shown, never kept or sold.
  public readonly ownJobs$: Observable<RenderJob[]> = this.jobs$.pipe(map((jobs) => RenderFlowService.ownJobs(jobs)));

  public static ownJobs(jobs: ReadonlyArray<RenderJob>): RenderJob[] {
    return jobs.filter((job) => job.source.kind !== 'sample');
  }

  public job$(modelId: string): Observable<RenderJob | undefined> {
    return this.jobs$.pipe(
      map((jobs) => jobs.find((item) => item.modelId === modelId)),
      distinctUntilChanged(),
    );
  }

  // Newest job first — "your previous renders" would start here.
  public get latestJob(): RenderJob | undefined {
    return [...this.jobs].sort((a, b) => b.updatedAt - a.updatedAt)[0];
  }

  public upload(modelId: string): RenderUploadModel | undefined {
    return this.uploads.get(modelId);
  }

  // Whether the jobs come from the backend's list (see listedJobs), not from this tab alone.
  public get hasBackendList(): boolean {
    return !!this.listSubscription;
  }

  // Resolves once the backend's list has arrived, or after `timeoutMs` without it.
  public whenListed(timeoutMs: number): Promise<void> {
    return new Promise((resolve) => {
      const timer = setTimeout(() => {
        subscription.unsubscribe();
        resolve();
      }, timeoutMs);
      const subscription = this.listedSubject.subscribe((listed) => {
        if (listed) {
          clearTimeout(timer);
          setTimeout(() => subscription.unsubscribe());
          resolve();
        }
      });
    });
  }

  public takeNotice(): RenderFlowNotice | null {
    const notice = this.notice;
    this.notice = null;
    return notice;
  }

  public setNotice(text: string, tone: RenderFlowNotice['tone'] = 'info'): void {
    this.notice = { text, tone };
  }

  public takePendingFiles(): RenderUploadFile[] | null {
    const files = this.pendingFiles;
    this.pendingFiles = null;
    return files;
  }

  public setPendingFiles(files: RenderUploadFile[]): void {
    this.pendingFiles = files;
    this.filesSubject.next();
  }

  public takePendingSample(): RenderSample | null {
    const sample = this.pendingSample;
    this.pendingSample = null;
    return sample;
  }

  public setPendingSample(sample: RenderSample): void {
    this.pendingSample = sample;
  }

  public takeReveal(modelId: string): boolean {
    const reveal = this.revealModelId === modelId;
    if (reveal) {
      this.revealModelId = null;
    }
    return reveal;
  }

  // The shell reports the model on screen (null on the upload step, the library, off the flow).
  // Looking at a model is what marks it seen.
  public setViewing(modelId: string | null): void {
    this.viewingModelId = modelId;
    const job = modelId ? this.job(modelId) : undefined;
    if (job) {
      if (job.unseen) {
        this.patch(job.modelId, { unseen: false });
      }
      this.markSeen(job);
    }
  }

  public static stepUrl(job: Pick<RenderJob, 'modelId' | 'step'>): string {
    return `/render/${job.step}/${job.modelId}`;
  }

  // The words on Library in the profile menu, and the tab's: "Preview ready", "Videos ready",
  // "2 ready"; failed: "Preview failed", "Videos stopped", "2 failed".
  public static readyText(ready: Pick<RenderReady, 'count' | 'what' | 'failed'>): string {
    if (ready.failed) {
      if (ready.count > 1) {
        return `${ready.count} failed`;
      }
      return ready.what === 'videos' ? 'Videos stopped' : 'Preview failed';
    }
    if (ready.count > 1) {
      return `${ready.count} ready`;
    }
    return ready.what === 'videos' ? 'Videos ready' : 'Preview ready';
  }

  public static isRendered(job: RenderJob): boolean {
    const previewFailed = job.step === 'error' && job.error?.failedStep === 'processing';
    return !!job.preview && job.step !== 'processing' && !previewFailed;
  }

  public static isStep(value: string | null | undefined): value is RenderFlowStep {
    return RENDER_FLOW_STEPS.includes(value as RenderFlowStep);
  }

  // --- upload and preview ---

  public setUploading(uploading: RenderUploading): void {
    this.uploadingSubject.next(uploading);
  }

  // Step 1's upload and the preview it starts, as one job from the first byte (owner, 2026-10-20:
  // the stages card opens as the upload begins, "Uploading" its first line): the model's page, the
  // header's ring and the library show it from then on. The upload runs here, not in a page, so
  // the user may move around the site while it does; only closing the tab stops it. A user's file
  // goes to the backend, whose renderer starts on the file's arrival — the page asks for nothing
  // more (decision 38). A sample already lives with us, so its upload is played rather than
  // performed: the walk-through shows the upload a real model gets (owner, 2026-09-23).
  // `note`: what the upload page found worth saying about the files ("From big.zip: 1 model and 14
  // textures."), kept with the job for its page. Returns the model's id for
  // /render/processing/:modelId, or null when the upload could not even start (takeUploadFailure).
  public beginUpload(input: RenderPreviewInput, note?: string): string | null {
    // An upload the backend has is known by the backend's id: its URL works in any tab.
    const backendId = 'upload' in input ? this.backend.uploadId(input.upload) : undefined;
    const modelId = backendId || RenderFlowService.newModelId();
    const now = Date.now();
    let source: RenderJobSource;
    let name: string;

    if ('sample' in input) {
      source = { kind: 'sample', sampleId: input.sample.id };
      name = input.sample.name;
      // One walk-through per sample: the library keeps none, so an earlier one would only linger.
      this.jobs
        .filter((job) => job.source.kind === 'sample' && job.source.sampleId === input.sample.id)
        .forEach((job) => {
          this.stopRun(job.modelId);
          this.remove(job.modelId);
        });
    } else {
      const upload = input.upload;
      source = {
        kind: 'upload',
        fileName: upload.fileName,
        format: upload.format,
        fileCount: upload.fileCount,
        textureCount: upload.textures.length,
        totalBytes: upload.totalBytes,
        ...(upload.fbx ? { modelTextures: upload.fbx.textures, exporter: upload.fbx.exporter } : {}),
      };
      name = upload.name;
      this.uploads.set(modelId, upload);
    }
    const totalBytes = 'sample' in input ? input.sample.fileBytes : input.upload.totalBytes;

    this.upsert({
      modelId,
      name,
      source,
      step: 'processing',
      progress: 0,
      statusText: 'Uploading…',
      uploading: { sentBytes: 0, totalBytes },
      ...(note ? { uploadNote: note } : {}),
      // $9 for everyone, members too (owner, 2026-10-14): a model is paid for on its own.
      entitlement: 'preview',
      createdAt: now,
      updatedAt: now,
    });
    if ('sample' in input) {
      this.track('sample_opened', this.job(modelId));
    } else {
      const upload = input.upload;
      this.track('upload_started', this.job(modelId), {
        format: upload.format,
        files: upload.fileCount,
        textures: upload.textures.length,
        size_mb: productMegabytes(upload.totalBytes),
        model_textures: upload.fbx?.textures,
        exporter: upload.fbx?.exporter?.app,
      });
    }
    const upload$: Observable<RenderUploadEvent> = 'sample' in input ? simulateUpload(totalBytes) : this.backend.uploadModel(input.upload);
    const run = upload$.subscribe({
      next: (event) => {
        if (event.type === 'progress') {
          // The bytes count up only: a chunk Storage sends again does not take them back.
          const before = this.job(modelId);
          const sentBytes = Math.max(before?.uploading?.sentBytes ?? 0, Math.min(event.sentBytes, event.totalBytes));
          const progress = Math.floor((sentBytes / (event.totalBytes || 1)) * RENDER_UPLOAD_BAR);
          this.patch(modelId, { uploading: { sentBytes, totalBytes: event.totalBytes }, progress: Math.max(before?.progress ?? 0, progress) });
          return;
        }
        // The whole model is there: the run takes the upload's place.
        this.runs.delete(modelId);
        const uploaded = this.patch(modelId, {
          uploading: undefined,
          backendId: event.backendId,
          progress: Math.max(this.job(modelId)?.progress ?? 0, RENDER_UPLOAD_BAR),
          statusText: `${RENDER_PREVIEW_STAGES[0].label}…`,
        });
        if (uploaded) {
          if (uploaded.source.kind === 'upload') {
            this.track('upload_completed', uploaded, { seconds: Math.round((Date.now() - now) / 1000), size_mb: productMegabytes(totalBytes) });
          }
          this.runPreview(uploaded);
        }
      },
      error: (error: Error) => this.failUpload(modelId, error),
    });
    // An upload refused on the spot has already taken its job away.
    if (!this.job(modelId)) {
      return null;
    }
    if (this.job(modelId)?.uploading) {
      this.runs.set(modelId, run);
    }
    return modelId;
  }

  // Why the last upload stopped, for the upload page when it never got going (beginUpload → null);
  // taking it takes its notice too, which the page then shows in its own place.
  public takeUploadFailure(): string | null {
    const reason = this.uploadFailure;
    this.uploadFailure = null;
    if (reason) {
      this.notice = null;
    }
    return reason;
  }

  // The upload stopped (the connection, the server): nothing reached the renderer, so there is no
  // model to follow. It goes, and the upload page says why — the user drops the model again.
  private failUpload(modelId: string, error: Error): void {
    this.track('upload_failed', this.job(modelId), { reason: productErrorCode(error) });
    const reason = error?.message || 'Try again.';
    this.runs.delete(modelId);
    this.remove(modelId);
    this.uploadFailure = reason;
    this.setNotice(`The upload stopped. ${reason}`, 'error');
  }

  // "Cancel and choose another model", and the library's stop of an upload: an upload still on its
  // way just stops (nothing renders before its last byte); a preview under way is stopped on the
  // backend and the model deleted. `announce`: say so on the next page (the processing page); the
  // library sees the card go.
  public cancelPreview(modelId: string, announce = true): void {
    const job = this.job(modelId);
    if (job) {
      this.track('model_deleted', job, { via: 'cancel', stage: job.uploading ? 'upload' : job.step });
    }
    this.stopRun(modelId);
    if (job?.uploading) {
      this.remove(modelId);
      if (announce) {
        this.setNotice('Upload cancelled. Nothing was rendered.');
      }
      return;
    }
    if (job) {
      this.removeWith(job, this.backend.cancel(job), 'cancel');
    } else {
      this.remove(modelId);
    }
    if (announce) {
      this.setNotice('Preview cancelled. No payment was taken.');
    }
  }

  // A failed preview, taken out of the library once its error has been seen (owner, 2026-10-01),
  // the way a cancel takes a preview: the backend stops whatever is left, the model goes. Only a
  // failed preview — failed videos were paid for, and go to support.
  // The user's name for a model (the downloads page's "File name", designer + owner, 2026-10-16):
  // the library, the pages and the pack's files go by it, and the backend keeps it where it keeps
  // the jobs. Taken here at once; a backend that fails to store it is only logged.
  public async renameJob(modelId: string, name: string): Promise<void> {
    const job = this.patch(modelId, { name });
    if (job) {
      await this.backend.rename(job, name).catch((error) => console.warn('Render flow: could not store the name', error));
    }
  }

  public canRemove(modelId: string): boolean {
    const job = this.job(modelId);
    return !!job && job.step === 'error' && job.error?.failedStep === 'processing';
  }

  // What the library's "−" takes out (Figma 28548:27569, owner 2026-10-22): a failed preview, and a
  // finished one nobody has paid for. Never a paid model, nor one in a checkout (a payment could
  // still land on it), nor one at work (its renderer would write it back).
  public canRemoveFromLibrary(modelId: string): boolean {
    const job = this.job(modelId);
    // A sample walked through, once it is not at work: nobody paid for it.
    const restingSample = !!job && job.source.kind === 'sample' && job.step !== 'processing' && job.step !== 'rendering';
    return this.canRemove(modelId) || restingSample || (!!job && job.step === 'preview' && job.entitlement !== 'full' && !job.uploading);
  }

  public removeFromLibrary(modelId: string): void {
    const job = this.job(modelId);
    if (!job || !this.canRemoveFromLibrary(modelId)) {
      return;
    }
    if (this.canRemove(modelId)) {
      this.removeFailed(modelId, false);
      return;
    }
    this.track('model_deleted', job, { via: 'library', stage: job.step });
    this.stopRun(modelId);
    this.removeWith(job, this.backend.discard(job), 'removing');
  }

  // `announce`: say so on the next page (from the error page); the library sees the card go.
  public removeFailed(modelId: string, announce = true): void {
    const job = this.job(modelId);
    if (!job || !this.canRemove(modelId)) {
      return;
    }
    this.track('model_deleted', job, { via: announce ? 'error_page' : 'library', stage: job.step });
    this.stopRun(modelId);
    this.removeWith(job, this.backend.cancel(job), 'removing');
    if (announce) {
      this.setNotice(`${job.name} was removed from your library. Nothing was charged.`);
    }
  }

  // There is no retry of a model (owner, 2026-10-20): the backend renders what was uploaded, once.
  // A failed run stays failed; the error screen's "Try again" takes the user back to the upload.
  // `resume`: after a reload — the backend's run goes on, the page follows it again.
  private runPreview(job: RenderJob, resume = false): void {
    this.stopRun(job.modelId);
    const upload = job.source.kind === 'upload' ? this.uploads.get(job.modelId) || null : null;
    const run = this.backend.runPreview(job, upload, resume).subscribe({
      next: (event: RenderRunEvent<RenderPreviewResult>) => {
        if (event.type === 'progress') {
          // The bar never goes back: not when the renderer's statuses do (a GPU job started over),
          // not when the run is followed again after a reload.
          const progress = Math.max(this.job(job.modelId)?.progress ?? 0, event.progress);
          this.patch(job.modelId, { progress, statusText: event.statusText, sides: event.sides });
          // The dev renderer reports where it stands, not how far: the bar creeps on from there.
          if (event.sides) {
            this.creepFrom(job.modelId, event.progress);
          }
          return;
        }
        this.creeps.delete(job.modelId);
        this.revealModelId = job.modelId;
        const ready = this.patch(job.modelId, {
          step: 'preview',
          progress: 100,
          statusText: 'Preview ready',
          preview: event.result.preview,
          standInSampleId: event.result.standInSampleId,
          error: undefined,
          unseen: this.isAway(job.modelId),
        });
        if (ready && !ready.unseen) {
          this.markSeen(ready);
        }
      },
      error: (error: Error) => this.fail(job.modelId, 'processing', 'We couldn’t prepare this preview.', error),
      complete: () => this.runs.delete(job.modelId),
    });
    this.runs.set(job.modelId, run);
  }

  // A new floor from the renderer starts the creep over from it; the same floor again keeps its
  // clock (a snapshot that changed nothing the bar goes by).
  private creepFrom(modelId: string, floor: number): void {
    if (this.creeps.get(modelId)?.floor !== floor) {
      this.creeps.set(modelId, { floor, since: Date.now() });
    }
    if (!this.creepTimer) {
      this.creepTimer = setInterval(() => this.creep(), CREEP_TICK_MS);
    }
  }

  // Each followed preview's bar where its stage's usual pace has brought it since the last report.
  // A job that left processing, or whose run stopped, leaves the creep.
  private creep(): void {
    const now = Date.now();
    this.creeps.forEach(({ floor, since }, modelId) => {
      const job = this.job(modelId);
      if (!job || job.step !== 'processing' || job.uploading || !this.runs.has(modelId)) {
        this.creeps.delete(modelId);
        return;
      }
      const progress = renderPreviewCreep(floor, now - since);
      if (progress > job.progress) {
        this.patch(modelId, { progress });
      }
    });
    if (!this.creeps.size) {
      clearInterval(this.creepTimer);
      this.creepTimer = undefined;
    }
  }

  // --- purchase ---

  // From the preview's order card. A model already paid for goes straight to the full render;
  // any other gets a checkout — a hosted one when the backend returns a URL, the stub page
  // otherwise — members too: a membership does not include the videos (owner, 2026-10-14).
  public async beginPurchase(modelId: string): Promise<RenderPurchaseNext> {
    const job = this.job(modelId);
    if (!job) {
      throw new Error('Unknown model');
    }
    // A sample is never sold: its videos are ours and already there (openSampleVideos).
    if (job.source.kind === 'sample') {
      throw new RenderCheckoutError('This is a sample: its finished videos are free to watch.');
    }
    this.track('purchase_clicked', job, { paid_already: job.entitlement === 'full' });
    if (job.entitlement === 'full') {
      this.startFullRender(modelId);
      return { kind: 'step', step: 'rendering' };
    }

    let session: RenderCheckoutSession;
    try {
      session = await this.backend.createCheckout(job);
    } catch (error) {
      this.track('checkout_failed', job, { reason: productErrorCode(error) });
      throw error;
    }
    this.patch(modelId, { checkoutError: undefined });
    if (session.url) {
      // Kept with the job, which outlives the trip to Stripe (sessionStorage): the return names the
      // session (checkoutReturnUrl).
      this.patch(modelId, { checkout: { sessionId: session.sessionId, at: Date.now() } });
      // What the user did up to here goes before the page leaves for Stripe (bounded: the
      // checkout never waits long for analytics).
      await this.productAnalytics.flushNow();
      return { kind: 'redirect', url: session.url };
    }
    // The stub checkout is the browser-only mock's: where Stripe is wired, no payment is ever faked.
    if (this.backend.kind !== 'mock') {
      this.track('checkout_failed', job, { reason: 'no_checkout_url' });
      throw new RenderCheckoutError('We couldn’t open the Stripe checkout. Try again, or contact support if it happens again.');
    }
    this.patch(modelId, { step: 'checkout' });
    return { kind: 'step', step: 'checkout' };
  }

  // A sample's finished videos, straight from its preview (owner, 2026-10-23): no checkout, no
  // render — the walk-through ends on the videos every model gets, and nothing is paid for.
  public openSampleVideos(modelId: string): void {
    const job = this.job(modelId);
    const sample = job?.source.kind === 'sample' ? renderSampleById(job.source.sampleId) : undefined;
    if (!job || !sample || job.step !== 'preview') {
      return;
    }
    this.patch(modelId, { step: 'downloads', videos: sample.videos, progress: 100 });
  }

  // The stub checkout's pay button, and the return from a hosted checkout (?session_id=…).
  public async confirmPayment(modelId: string, sessionId: string): Promise<void> {
    const job = this.job(modelId);
    if (!job || job.source.kind === 'sample') {
      return;
    }
    try {
      const entitlement = await this.backend.confirmPayment(job, sessionId);
      this.patch(modelId, { entitlement, checkoutError: undefined });
      // Until the payment webhook records it on the backend, the page does (open question 3).
      this.remember(job, { entitlement });
      // The render's page comes in the way the preview does (takeReveal), not all at once.
      this.revealModelId = modelId;
      this.startFullRender(modelId);
    } catch (error) {
      if (error instanceof RenderNotImplementedError) {
        throw error;
      }
      const reason = (error as Error)?.message;
      if (reason === 'pending') {
        this.patch(modelId, { step: 'confirming', checkoutError: undefined });
        return;
      }
      this.patch(modelId, { step: 'checkout', checkoutError: reason === 'declined' ? 'declined' : reason || 'failed' });
    }
  }

  public cancelCheckout(modelId: string): void {
    this.patch(modelId, { step: 'preview', checkoutError: undefined });
    this.setNotice('You returned to your preview. No payment was taken.');
  }

  // Back from Stripe's page: /success?session_id=… once paid, /cancel without paying (the
  // addresses purchases-back gives Stripe, at the site's root). The model is the one that checkout
  // was opened for — this tab's job, or the backend's list in a tab without it (the return lands
  // on the dev site, whichever address the checkout left from); a cancel names no session, so it
  // is the last checkout opened. Null when there is none: not the render flow's return.
  public async checkoutReturnUrl(paid: boolean, sessionId: string): Promise<string | null> {
    const find = (): RenderJob | undefined => paid
      ? this.jobs.find((item) => !!sessionId && item.checkout?.sessionId === sessionId)
      : this.jobs
        .filter((item) => item.checkout && Date.now() - item.checkout.at < CHECKOUT_RETURN_MS)
        .sort((a, b) => (b.checkout?.at || 0) - (a.checkout?.at || 0))[0];
    let job = find();
    if (!job && this.hasBackendList) {
      await this.whenListed(CHECKOUT_LIST_WAIT_MS);
      job = find();
    }
    this.track('checkout_returned', job, { kind: paid ? 'success' : 'cancel', found: !!job });
    if (!job) {
      return null;
    }
    // Further along already (the paid run under way, the videos ready): that step.
    if (job.step !== 'preview' && job.step !== 'checkout' && job.step !== 'confirming') {
      return RenderFlowService.stepUrl(job);
    }
    if (!paid) {
      this.cancelCheckout(job.modelId);
      return RenderFlowService.stepUrl({ modelId: job.modelId, step: 'preview' });
    }
    this.patch(job.modelId, { step: 'confirming', checkoutError: undefined });
    // Stripe comes back here only once the card is charged: the payment is confirmed before the page
    // opens, so the return lands on the render itself instead of the checkout's layout flashing
    // first (owner, 2026-10-23). Only a confirmation still pending opens the confirming step, which
    // goes on asking with the session; a failed one opens the checkout and says why.
    await this.confirmPayment(job.modelId, sessionId).catch(() => undefined);
    const current = this.job(job.modelId);
    if (current && current.step !== 'confirming') {
      return RenderFlowService.stepUrl(current);
    }
    return `/render/confirming/${job.modelId}?session_id=${encodeURIComponent(sessionId)}`;
  }

  // --- full render ---

  // `resume`: after a reload, following the render that is already under way (its progress so far
  // stays on screen until the backend reports).
  public startFullRender(modelId: string, resume = false): void {
    // Already rendering (followed again): its bar stays where it is.
    const rendering = this.job(modelId)?.step === 'rendering';
    const job = resume || rendering
      ? this.patch(modelId, { step: 'rendering', error: undefined })
      : this.patch(modelId, { step: 'rendering', progress: 0, statusText: RENDER_VIDEO_LINES.setup, error: undefined });
    if (!job) {
      return;
    }
    this.stopRun(modelId);
    const run = this.backend.runFullRender(job, resume).subscribe({
      next: (event: RenderRunEvent<RenderVideosResult>) => {
        if (event.type === 'progress') {
          this.patch(modelId, { progress: Math.max(this.job(modelId)?.progress ?? 0, event.progress), statusText: event.statusText, eta: event.eta });
          return;
        }
        const ready = this.patch(modelId, {
          step: 'downloads',
          progress: 100,
          statusText: 'Videos ready',
          videos: event.result.videos,
          // The list keeps this tab's job while the render runs, the preview's stills with it.
          ...(event.result.preview ? { preview: event.result.preview } : {}),
          unseen: this.isAway(modelId),
        });
        if (ready && !ready.unseen) {
          this.markSeen(ready);
        }
      },
      error: (error: Error) => this.fail(modelId, 'rendering', 'We couldn’t finish the videos.', error),
      complete: () => this.runs.delete(modelId),
    });
    this.runs.set(modelId, run);
  }

  // --- housekeeping ---

  // Starts this tab over. What the backend lists stays: those are the user's models, not the tab's.
  public resetAll(): void {
    this.runs.forEach((run) => run.unsubscribe());
    this.runs.clear();
    this.uploads.clear();
    this.notice = null;
    this.jobsSubject.next([]);
    this.persist();
    if (this.listedJobs) {
      this.reconcile(this.listedJobs);
    }
  }

  // --- which backend runs the flow ---

  public get backendKind(): RenderBackendKind {
    return this.backend.kind;
  }

  public get isMock(): boolean {
    return this.backend.kind === 'mock';
  }

  // DEV: real uploads and renders on the dev render backend, plus DEV buttons (FirestoreRenderBackend).
  public get isSimulator(): boolean {
    return this.backend.kind === 'simulator';
  }

  // --- product analytics ---

  // A step of the user's for the product analytics (ProductAnalyticsService), here and from the
  // pages: the model by the backend's id — before its upload ends, the id the page already goes by,
  // which is the same one — and a sample by its own id. The mock's models have no backend id and go
  // without one.
  public track(name: ProductEventName, job: RenderJob | undefined, params: ProductEventParams = {}): void {
    if (job?.source.kind === 'sample') {
      this.productAnalytics.track(name, { params: { ...params, sample: job.source.sampleId } });
      return;
    }
    const modelId = job && !this.isMock ? job.backendId || job.modelId : undefined;
    this.productAnalytics.track(name, { modelId, params });
  }

  // --- internals ---

  // A failure is news too: a preview that failed while you were away needs you as much as one that
  // is ready.
  private fail(modelId: string, failedStep: 'processing' | 'rendering', title: string, error: Error): void {
    this.runs.delete(modelId);
    const unseen = this.isAway(modelId);
    // Cancelled in another tab: the model is simply gone here too (the shell leaves its page).
    if (error instanceof RenderModelGoneError) {
      if (!unseen) {
        this.setNotice('This model was cancelled in another tab. Start with a new one.');
      }
      this.remove(modelId);
      return;
    }
    if (!unseen) {
      const job = this.job(modelId);
      if (job) {
        this.markSeen({ ...job, step: 'error' });
      }
    }
    if (error instanceof RenderNotImplementedError) {
      // Live backend without a backend: the page explains instead of an endless spinner.
      this.patch(modelId, { step: 'error', error: { title: 'Not wired up yet.', text: error.message, failedStep }, unseen });
      return;
    }
    console.error(`Render flow: ${failedStep} failed for ${modelId}`, error);
    const blame = error instanceof RenderRunError ? error.blame : 'service';
    const about = error instanceof RenderRunError ? error.about : undefined;
    const progress = this.job(modelId)?.progress;
    this.patch(modelId, { step: 'error', error: { title, text: error?.message || 'Something went wrong.', failedStep, blame, progress, ...(about ? { about } : {}) }, unseen });
  }

  private isAway(modelId: string): boolean {
    return this.viewingModelId !== modelId;
  }

  // The backend's list, laid over this tab's jobs. A job with an upload or a run of its own in this
  // tab keeps it; any other takes what the list says, and one in progress gets its run (another tab
  // started it, or the tab is new). Checkout and confirming are this tab's own: the backend only
  // knows the preview is there.
  private reconcile(everything: RenderJob[]): void {
    this.listedJobs = everything;
    // A model on its way out (`leaving`) stays off until its document is gone from the list.
    this.leaving.forEach((id) => everything.some((job) => job.modelId === id) || this.leaving.delete(id));
    const listed = everything.filter((job) => !this.leaving.has(job.modelId));
    const byId = new Map(listed.map((job) => [job.modelId, job]));
    const resume: RenderJob[] = [];
    let jobs = this.jobs.filter((job) => {
      const id = job.backendId;
      // Gone from the list after being on it: deleted elsewhere. Or an upload of an older build,
      // under a local id, that the list now shows under the backend's.
      return !(id && ((this.everListed.has(id) && !byId.has(id)) || (id !== job.modelId && byId.has(id))));
    });
    for (const item of listed) {
      this.everListed.add(item.modelId);
      const index = jobs.findIndex((job) => job.modelId === item.modelId);
      const local = index >= 0 ? jobs[index] : undefined;
      if (local && this.runs.has(local.modelId)) {
        // The run here reports the progress; the list, what the run does not: the entitlement, and
        // while the videos render the frames — each clean one in place of the watermarked as it comes.
        const frames = local.step === 'rendering' && item.step === 'rendering' && item.preview ? { preview: item.preview } : {};
        jobs[index] = { ...local, entitlement: item.entitlement, ...frames };
        continue;
      }
      const inCheckout = !!local && (local.step === 'checkout' || local.step === 'confirming') && item.step === 'preview';
      const viewing = this.viewingModelId === item.modelId;
      // The list knows where the renderer stands, the tab how far its bar got: a step still under way
      // keeps the bar it had.
      const sameStep = !!local && local.step === item.step && (item.step === 'processing' || item.step === 'rendering');
      const next: RenderJob = {
        ...local,
        ...item,
        step: inCheckout && local ? local.step : item.step,
        progress: sameStep && local ? Math.max(local.progress, item.progress) : item.progress,
        checkoutError: inCheckout ? local?.checkoutError : undefined,
        createdAt: local?.createdAt || item.createdAt,
        updatedAt: local?.updatedAt || item.updatedAt,
        unseen: !!item.unseen && !viewing,
      };
      if (index >= 0) {
        jobs[index] = next;
      } else {
        jobs = [...jobs, next];
      }
      if (viewing && item.unseen) {
        this.markSeen(next);
      }
      if (!inCheckout && (next.step === 'processing' || next.step === 'rendering')) {
        resume.push(next);
      }
    }
    this.jobsSubject.next(jobs);
    this.persist();
    for (const job of resume) {
      if (job.step === 'processing') {
        this.runPreview(job, true);
      } else {
        this.startFullRender(job.modelId, true);
      }
    }
    this.listedSubject.next(true);
  }

  private remember(job: RenderJob, facts: RenderJobFacts): void {
    this.backend.remember(job, facts).catch((error) => console.warn(`Render flow: could not store ${JSON.stringify(facts)} for ${job.modelId}`, error));
  }

  // The user has looked at this step of the model: the "new" dot goes on every device.
  private markSeen(job: RenderJob): void {
    if (job.step === 'preview' || job.step === 'downloads' || job.step === 'error') {
      this.remember(job, { seenStep: job.step });
    }
  }

  private stopRun(modelId: string): void {
    this.runs.get(modelId)?.unsubscribe();
    this.runs.delete(modelId);
  }

  private upsert(job: RenderJob): void {
    const others = this.jobs.filter((item) => item.modelId !== job.modelId);
    this.jobsSubject.next([...others, job]);
    this.persist();
  }

  private patch(modelId: string, changes: Partial<RenderJob>): RenderJob | undefined {
    const current = this.job(modelId);
    if (!current) {
      return undefined;
    }
    const next: RenderJob = { ...current, ...changes, updatedAt: Date.now() };
    this.jobsSubject.next(this.jobs.map((item) => (item.modelId === modelId ? next : item)));
    this.persist();
    return next;
  }

  private remove(modelId: string): void {
    this.uploads.delete(modelId);
    this.jobsSubject.next(this.jobs.filter((item) => item.modelId !== modelId));
    this.persist();
  }

  // A model the backend is deleting (`removal`): off the page at once and kept off while the list
  // still shows it (`leaving`). A removal that failed puts it back as the list has it.
  private removeWith(job: RenderJob, removal: Promise<void>, what: string): void {
    const id = job.backendId;
    if (id) {
      this.leaving.add(id);
    }
    this.remove(job.modelId);
    removal.catch((error) => {
      console.error(`Render flow: ${what} ${job.modelId} failed`, error);
      if (id && this.leaving.delete(id) && this.listedJobs) {
        this.reconcile(this.listedJobs);
      }
    });
  }

  private persist(): void {
    try {
      const state: StoredState = { jobs: this.jobs };
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(state));
    } catch {
      // Private mode / blocked storage: the flow still works for this page load.
    }
  }

  // Jobs from before a reload. A run that was in flight is picked up again: the mock's timers start
  // over, a backend's run is followed from where it is.
  private restore(): void {
    let stored: StoredState | null = null;
    try {
      stored = JSON.parse(sessionStorage.getItem(STORAGE_KEY) || 'null');
    } catch {
      stored = null;
    }
    if (!stored || !Array.isArray(stored.jobs)) {
      return;
    }
    // A job of a sample retired since (the revolver, 2026-10-16) goes with it: its frames and
    // videos are gone.
    const valid = stored.jobs.filter((job) => job && typeof job.modelId === 'string' && RenderFlowService.isStep(job.step)
      && (job.source?.kind !== 'sample' || !!renderSampleById(job.source.sampleId))
      && (!job.standInSampleId || !!renderSampleById(job.standInSampleId)));
    // An upload still on its way when the page went (the browser asked first) died with it: its
    // last bytes never arrived, so nothing renders. It goes, and the next page says so.
    const lost = valid.filter((job) => job.uploading);
    const jobs = valid.filter((job) => !job.uploading).map((job) => RenderFlowService.unsold(job));
    if (lost.length) {
      this.setNotice(lost.length === 1 ? `The upload of ${lost[0].name} stopped when the page closed. Upload it again.` : 'Uploads stopped when the page closed. Upload the models again.', 'error');
    }
    this.jobsSubject.next(jobs);

    for (const job of jobs) {
      if (job.step === 'processing') {
        this.runPreview(job, true);
      } else if (job.step === 'rendering') {
        this.startFullRender(job.modelId, true);
      }
    }
  }

  // A sample job stored by a build that sold samples (until 2026-10-23): one left in a checkout goes
  // back to its preview, one already paid for or rendering lands on its videos, and none stays
  // "paid for".
  private static unsold(job: RenderJob): RenderJob {
    if (job.source?.kind !== 'sample') {
      return job;
    }
    const sample = renderSampleById(job.source.sampleId);
    const toVideos = job.step === 'rendering' || (job.step === 'confirming' && job.entitlement === 'full');
    const step = toVideos ? 'downloads' : job.step === 'checkout' || job.step === 'confirming' ? 'preview' : job.step;
    return {
      ...job,
      step,
      entitlement: 'preview',
      checkout: undefined,
      checkoutError: undefined,
      ...(step === 'downloads' && sample ? { videos: sample.videos, progress: 100 } : {}),
    };
  }

  private static newModelId(): string {
    return `m${Date.now().toString(36)}${Math.random().toString(36).slice(2, 8)}`;
  }
}
